import type { GameContext } from '../core/context';
import type { CrimeType } from '../core/events';
import type { V2 } from '../core/math';
import type { Rng } from '../core/rng';
import type { System } from '../core/system';
import type { PlayerService } from '../player/playerSystem';
import type { PoliceService } from '../police/types';
import type { CityLayout } from '../world/types';
import { GUNSHOT_EARSHOT, HEARD_CRIMES, HeatModel, WARNING_MEMORY, isMinorOffence, type Witnesses } from './heatModel';
import { applyServerHeat, localHeatAuthority } from './onlineHeat';
import type { HeatInfo, HeatService } from './types';
import { countWitnesses, recognitionRange, type Observer } from './witness';

/**
 * Heat system (order 520, rules band): turns `crime` events into wanted level using the pure
 * HeatModel, decides each step whether any officer can see (and recognises) the player, tracks cover
 * zones, stolen-car reports and reckless driving under police noses. Owns `ctx.state.heat`.
 */

const CIVILIAN_RANGE = 28;
const DRIVER_RANGE = 22;
/** A quiet car theft (no carjack) is only noticed by passers-by this close: a side-street swap can go unseen. */
const QUIET_THEFT_RANGE = 14;
/** A theft nobody saw is phoned in by the owner after this long; a witnessed one almost at once. */
const REPORT_DELAY_UNSEEN = 35;
const REPORT_DELAY_SEEN = 4;
const RECKLESS_SPEED = 20;
const RECKLESS_RANGE = 30;
const RECKLESS_COOLDOWN = 6;
const SIGHT_INTERVAL = 3; // fixed steps between sight checks
const HIDE_STILL_TIME = 1.2;
/** During a search, a civilian who sees the (matching) suspect phones it in with this chance per check. */
const TIP_OFF_CHANCE = 0.3;
const TIP_OFF_INTERVAL = 1.5;
const TIP_OFF_RANGE = 22;

interface CoverZone {
  kind: 'segment' | 'circle';
  a: V2;
  b: V2;
  r: number;
}

export function createHeatSystem(): System {
  let ctx: GameContext;
  let layout: CityLayout | undefined;
  const model = new HeatModel();
  let seen = false;
  let hidden = false;
  let lastKnownVel: V2 | null = null;
  let stillFor = 0;
  let inAlley = false;
  let recklessReadyAt = 0;
  let tipReadyAt = 0;
  let rng: Rng;
  let forcedWitness: 'auto' | 'police' | 'civilian' | 'none' = 'auto';
  let serverAuthority = false;
  /** Online: the server scores crimes, but the warning cue is local (the server applies the same rule). */
  let onlineWarnedUntil = -Infinity;
  const reports = new Map<number, number>();
  const reportUsed = new Set<number>();
  const cover: CoverZone[] = [];
  const alleys: CoverZone[] = [];
  const seers: number[] = [];
  const offs: (() => void)[] = [];

  const player = (): PlayerService | undefined => ctx.services.tryGet('player');
  const police = (): PoliceService | undefined => ctx.services.tryGet('police');

  function playerVehicleId(): number | null {
    const p = player();
    return p && p.mode === 'driving' && p.vehicle ? p.vehicle.id : null;
  }

  function playerPos(): V2 {
    const p = player();
    return p ? p.pos : (layout?.playerSpawn.pos ?? { x: 0, z: 0 });
  }

  const los = (a: V2, b: V2): boolean => ctx.physics.lineOfSight(a, b);

  function observersAt(pos: V2): Observer[] {
    const out: Observer[] = [];
    for (const o of police()?.observers() ?? []) out.push({ x: o.x, z: o.z, kind: 'police', range: o.range });
    ctx.services.tryGet('peds')?.forEachNear(pos.x, pos.z, CIVILIAN_RANGE, (x, z) => out.push({ x, z, kind: 'civilian', range: CIVILIAN_RANGE }));
    const playerVeh = playerVehicleId();
    ctx.services.tryGet('vehicleGrid')?.forEachNear(pos.x, pos.z, DRIVER_RANGE + 3, (v) => {
      if (v.isPolice || v.id === playerVeh || v.occupant?.kind !== 'ped') return;
      out.push({ x: v.pos.x, z: v.pos.z, kind: 'civilian', range: DRIVER_RANGE });
    });
    return out;
  }

  function witnessesFor(pos: V2, mode: 'auto' | 'police' | 'civilian' | 'none', type?: CrimeType): Witnesses {
    if (mode === 'police') return { police: 1, civilians: 0 };
    if (mode === 'civilian') return { police: 0, civilians: 3 };
    if (mode === 'none') return { police: 0, civilians: 0 };
    let obs = observersAt(pos);
    if (type === 'carTheft') obs = obs.map((o) => (o.kind === 'civilian' ? { ...o, range: Math.min(o.range, QUIET_THEFT_RANGE) } : o));
    const w = countWitnesses(pos, obs, los);
    if (type && HEARD_CRIMES.has(type)) {
      // Gunfire carries through walls: everybody in earshot is a caller, and behind the terraces
      // somebody always is (at least one), so a shot is reported even on an empty street.
      let ears = 0;
      ctx.services.tryGet('peds')?.forEachNear(pos.x, pos.z, GUNSHOT_EARSHOT, () => ears++);
      w.civilians = Math.max(w.civilians, Math.min(ears, 6), 1);
    }
    return w;
  }

  function score(type: CrimeType, severity: number, pos: V2, mode: 'auto' | 'police' | 'civilian' | 'none'): number {
    if (serverAuthority) {
      // The server scores crimes (and tells us the result in `heat`); we only report who saw it.
      const w = witnessesFor(pos, mode, type);
      ctx.events.emit('crimeWitnessed', { type, severity, police: w.police, civilians: w.civilians });
      if (model.level === 0 && w.police > 0 && isMinorOffence(type, severity) && ctx.state.time >= onlineWarnedUntil) {
        onlineWarnedUntil = ctx.state.time + WARNING_MEMORY;
        ctx.events.emit('policeWarning', { pos: { ...playerPos() }, type });
      }
      return 0;
    }
    const beforePhase = model.phase;
    const w = witnessesFor(pos, mode, type);
    const vehicleId = playerVehicleId();
    if ((type === 'carTheft' || type === 'carjack' || type === 'policeCarTheft') && vehicleId !== null && !reports.has(vehicleId)) {
      const seenBy = w.police + w.civilians > 0;
      reports.set(vehicleId, ctx.state.time + (seenBy ? REPORT_DELAY_SEEN : REPORT_DELAY_UNSEEN));
    }
    const pts = model.addCrime(type, severity, w, { pos: { ...playerPos() }, time: ctx.state.time, vehicleId });
    if (pts > 0) ctx.events.emit('heatCrime', { type, points: +pts.toFixed(2), police: w.police, civilians: w.civilians, level: model.level });
    // A first slip in front of a copper while not wanted: a word, not a chase (docs/POLICE.md §1).
    if (model.lastWarning) ctx.events.emit('policeWarning', { pos: { ...playerPos() }, type });
    if (model.phase === 'pursuit' && beforePhase !== 'pursuit') ctx.events.emit('policeSpotted', { pos: { ...playerPos() }, level: model.level });
    if (model.phase === 'search' && beforePhase !== 'search' && model.search) {
      ctx.events.emit('searchStarted', { center: { ...model.search.center }, radius: model.search.radius, level: model.level });
    }
    syncLevel();
    return pts;
  }

  /** Mirror the model level into `ctx.state.heat` (owned by this module) and announce changes. */
  function syncLevel(): void {
    if (ctx.state.heat !== model.level) {
      const prev = ctx.state.heat;
      ctx.state.heat = model.level;
      ctx.events.emit('heatChanged', { level: model.level, previous: prev });
    }
  }

  function nearSegment(p: V2, z: CoverZone): boolean {
    const ex = z.b.x - z.a.x;
    const ez = z.b.z - z.a.z;
    const l2 = ex * ex + ez * ez || 1;
    const t = Math.max(0, Math.min(1, ((p.x - z.a.x) * ex + (p.z - z.a.z) * ez) / l2));
    return Math.hypot(z.a.x + ex * t - p.x, z.a.z + ez * t - p.z) <= z.r;
  }

  function inZone(p: V2, zones: readonly CoverZone[]): boolean {
    for (const z of zones) {
      if (z.kind === 'circle' ? Math.hypot(p.x - z.a.x, p.z - z.a.z) <= z.r : nearSegment(p, z)) return true;
    }
    return false;
  }

  /** Can any officer (but `except`) see and recognise the player? Fills `out` with their unit ids. */
  function sightOf(pol: PoliceService, out: number[], except = -1): void {
    const p = playerPos();
    const matches = model.matchesDescription(playerVehicleId());
    for (const o of pol.observers()) {
      if (o.unitId === except) continue;
      const range = model.level === 0 ? o.range : recognitionRange(o.range, matches, hidden, inAlley);
      const dx = o.x - p.x;
      const dz = o.z - p.z;
      if (dx * dx + dz * dz > range * range) continue;
      if (!los({ x: o.x, z: o.z }, p)) continue;
      out.push(o.unitId);
    }
  }

  /** Can any officer see and recognise the player? Fills `seers`. */
  function checkSight(): boolean {
    seers.length = 0;
    const pol = police();
    if (!pol) return false;
    sightOf(pol, seers);
    pol.setSeers(seers);
    return seers.length > 0;
  }

  /**
   * A pursuing unit crashed out by its own driving ("laid on the tarmac", docs/POLICE.md §14): its sighting
   * goes with it; if nobody else has eyes on you the search starts at once, and the N-th at level N takes a
   * level off. Offline only — online the server applies the same rule (server/police/heat.ts).
   */
  function unitDown(unitId: number, pos: V2): void {
    const pol = police();
    if (serverAuthority || !pol || model.level === 0) return;
    const others: number[] = [];
    sightOf(pol, others, unitId);
    const before = model.level;
    const r = model.unitDown(others.length > 0);
    if (!r.counted) return;
    if (r.searchNow) {
      seen = false;
      if (model.search) ctx.events.emit('searchStarted', { center: { ...model.search.center }, radius: model.search.radius, level: model.level });
    }
    ctx.events.emit('unitDown', { pos: { ...pos }, level: model.level, downs: r.downs, needed: Number.isFinite(r.needed) ? r.needed : 0, dropped: r.dropped || model.level < before, searchNow: r.searchNow });
    syncLevel();
  }

  const service: HeatService = {
    get level() {
      return model.level;
    },
    get points() {
      return model.points;
    },
    get phase() {
      return model.phase;
    },
    get seen() {
      return seen;
    },
    get hidden() {
      return hidden;
    },
    get search() {
      const s = model.search;
      return s ? { center: s.center, radius: s.radius, progress: model.searchProgress } : null;
    },
    get lastKnown() {
      return model.suspect ? model.suspect.pos : null;
    },
    get lastKnownVel() {
      return model.suspect ? lastKnownVel : null;
    },
    get descriptionMatches() {
      return model.matchesDescription(playerVehicleId());
    },
    info(): HeatInfo {
      const s = model.search;
      const sus = model.suspect;
      return {
        level: model.level,
        points: +model.points.toFixed(2),
        phase: model.phase,
        seen,
        hidden,
        search: s ? { x: +s.center.x.toFixed(2), z: +s.center.z.toFixed(2), radius: +s.radius.toFixed(1), progress: +model.searchProgress.toFixed(3) } : null,
        suspect: sus ? { x: +sus.pos.x.toFixed(2), z: +sus.pos.z.toFixed(2), vehicleId: sus.vehicleId, age: +(ctx.state.time - sus.time).toFixed(2) } : null,
        pursuitTime: +model.pursuitTime.toFixed(2),
      };
    },
    setLevel(level) {
      model.setLevel(level, level > 0 ? { pos: { ...playerPos() }, time: ctx.state.time, vehicleId: playerVehicleId() } : null);
      syncLevel();
    },
    addCrime(type, severity, pos, witness = 'auto') {
      return score(type, severity, pos, witness);
    },
    clear() {
      model.clear();
      seen = false;
      syncLevel();
    },
    dropOne() {
      if (serverAuthority || model.level === 0) return model.level;
      const before = model.level;
      const now = model.dropOne();
      if (now === 0) {
        seen = false;
        ctx.events.emit('heatCleared', { previous: before });
      }
      syncLevel();
      return now;
    },
    isReported(vehicleId) {
      const t = reports.get(vehicleId);
      return t !== undefined && ctx.state.time >= t;
    },
    get serverAuthority() {
      return serverAuthority;
    },
    setServerAuthority(on) {
      serverAuthority = on;
      if (!on) {
        model.clear();
        seen = false;
        syncLevel();
      }
    },
    applyServer(msg) {
      applyServerHeat(model, msg, ctx.state.time);
      if (msg.level <= 0) seen = false;
      syncLevel();
      // A unit chasing you crashed out (the server judged it; docs/POLICE.md §14): the same stamp as offline.
      if (msg.down) {
        const d = msg.down;
        ctx.events.emit('unitDown', { pos: null, level: msg.level, downs: d.n, needed: d.need, dropped: d.drop === 1, searchNow: d.lost === 1 });
      }
    },
  };

  return {
    id: 'heat',
    order: 520,
    init(c) {
      ctx = c;
      rng = c.rng.fork('heat');
      layout = c.services.tryGet('layout');
      c.services.provide('heat', service);
      offs.push(
        c.events.on('crime', (e) => {
          if (!e.byPlayer) {
            police()?.reportIncident(e.pos, e.type, e.severity);
            return;
          }
          const mode = forcedWitness;
          forcedWitness = 'auto';
          score(e.type, e.severity, e.pos, mode);
        }),
        c.events.on('vehicleDestroyed', (e) => {
          if (!e.byPlayer) {
            police()?.reportIncident(e.pos, 'wreck');
            return;
          }
          // Wrecking somebody else's car (worse: a police car) is destruction. The ride the player
          // stole or owns burning out is not scored again.
          const v = ctx.services.tryGet('vehicles')?.get(e.vehicleId);
          if (!v || v.ownedByPlayer || v.stolen || v.controller === 'player') return;
          score('destruction', v.isPolice ? 5 : 3, e.pos, 'auto');
        }),
        c.events.on('policeUnitDisabled', (e) => {
          if (e.forced) unitDown(e.unitId, e.pos);
        }),
      );
      c.hooks.register('setHeat', (level) => service.setLevel(level));
      c.hooks.register('getHeat', () => service.info());
      c.hooks.register('forceCrime', (type, severity = 3, witness = 'auto') => {
        const before = model.points;
        forcedWitness = witness;
        ctx.events.emit('crime', { type: type as CrimeType, pos: { ...playerPos() }, severity, byPlayer: true });
        forcedWitness = 'auto';
        return +(model.points - before).toFixed(3);
      });
      c.hooks.registerSnapshot('heat', () => service.info());
    },
    start() {
      layout = ctx.services.tryGet('layout') ?? layout;
      if (!layout) return;
      const nodes = layout.roads.nodes;
      for (const e of layout.roads.edges) {
        if (!e.alley) continue;
        const zone: CoverZone = { kind: 'segment', a: nodes[e.from]!.pos, b: nodes[e.to]!.pos, r: e.width / 2 + 1 };
        alleys.push(zone);
        cover.push(zone);
      }
      for (const poi of layout.pois) {
        if (poi.kind === 'lockup' || poi.kind === 'garage' || poi.kind === 'hideout' || poi.kind === 'safehouse') {
          cover.push({ kind: 'circle', a: poi.pos, b: poi.pos, r: poi.radius + 2.5 });
        }
      }
    },
    fixedUpdate(dt) {
      const p = player();
      const pos = playerPos();
      const speed = p ? Math.hypot(p.velocity.x, p.velocity.z) : 0;

      // Cover: still, inside a lockup / garage / alley.
      inAlley = alleys.length > 0 && inZone(pos, alleys);
      if (speed < 0.6 && inZone(pos, cover)) stillFor += dt;
      else stillFor = 0;
      hidden = stillFor >= HIDE_STILL_TIME;

      if (ctx.state.frame % SIGHT_INTERVAL === 0) seen = checkSight();

      const vehicleId = playerVehicleId();
      if (seen) {
        // Stolen car reported earlier, now seen by a copper: that's a pull.
        if (vehicleId !== null && model.level === 0 && service.isReported(vehicleId) && !reportUsed.has(vehicleId)) {
          reportUsed.add(vehicleId);
          score('carTheft', 2, pos, 'police');
        }
        // Tearing past a police car. Only while nobody is after you yet: once you're wanted, fleeing
        // fast is already scored by the chase escalation (heatModel CHASE_ESCALATION); charging it again
        // as a crime every 6 s scored the same getaway twice (docs/POLICE.md §12).
        if (vehicleId !== null && speed > RECKLESS_SPEED && ctx.state.time >= recklessReadyAt && model.level === 0) {
          const pol = police();
          const near = pol?.observers().some((o) => seers.includes(o.unitId) && Math.hypot(o.x - pos.x, o.z - pos.z) < RECKLESS_RANGE);
          if (near) {
            recklessReadyAt = ctx.state.time + RECKLESS_COOLDOWN;
            score('recklessDriving', 2, pos, 'police');
          }
        }
        if (model.level > 0) {
          model.sighting({ pos: { ...pos }, time: ctx.state.time, vehicleId });
          lastKnownVel = p ? { x: p.velocity.x, z: p.velocity.z } : null;
        }
      }

      // Tip-offs: in a search, busy streets give you away; alleys and cover don't.
      if (!serverAuthority && model.phase === 'search' && !hidden && !inAlley && ctx.state.time >= tipReadyAt) {
        tipReadyAt = ctx.state.time + TIP_OFF_INTERVAL;
        if (model.matchesDescription(vehicleId) && rng.chance(TIP_OFF_CHANCE)) {
          const w = countWitnesses(pos, observersAt(pos).filter((o) => o.kind === 'civilian').map((o) => ({ ...o, range: Math.min(o.range, TIP_OFF_RANGE) })), los, 4);
          if (w.civilians > 0 && model.tipOff(pos, ctx.state.time, vehicleId)) {
            lastKnownVel = p ? { x: p.velocity.x, z: p.velocity.z } : null;
            ctx.events.emit('sightingReported', { pos: { ...pos }, level: model.level });
          }
        }
      }

      const before = model.level;
      const beforePhase = model.phase;
      // Online the server decides when heat decays and when the police give up (docs/POLICE.md §10).
      const gaveUp = localHeatAuthority(serverAuthority) ? model.update(dt, { seen: seen && model.level > 0, hidden, playerSpeed: speed }) : false;
      if (beforePhase !== 'pursuit' && model.phase === 'pursuit') ctx.events.emit('policeSpotted', { pos: { ...pos }, level: model.level });
      if (beforePhase === 'pursuit' && model.phase === 'search' && model.search) {
        ctx.events.emit('searchStarted', { center: { ...model.search.center }, radius: model.search.radius, level: model.level });
      }
      // `escaped`: they lost you (not a bribe or a desk sergeant) — "Your record" keeps the best getaway.
      if (gaveUp) ctx.events.emit('heatCleared', { previous: before, escaped: true });
      syncLevel();
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
